import {trackAuthEvent} from '@/shared/utils/userManager';

interface ExpiringUser {
    expired?: boolean;
    expires_in?: number;
}

interface SessionUserManager {
    getUser(): Promise<ExpiringUser | null>;
    signinSilent(): Promise<unknown>;
    removeUser(): Promise<void>;
    events: {
        addUserLoaded(callback: (user: ExpiringUser) => void): void;
        addUserUnloaded(callback: () => void): void;
        addAccessTokenExpired(callback: () => void): void;
        addSilentRenewError(callback: (error: unknown) => void): void;
    };
}

interface CheckOptions {
    // The page was signed in before, so a missing user means the session is gone.
    signedIn?: boolean;
}

export interface SessionExpiry {
    check(options?: CheckOptions): Promise<void>;
}

// How long a check waits for the automatic renew of oidc-client to finish (its
// silent request timeout is 10 s), so it doesn't start a second renew next to
// it. The refresh tokens are reusable (ReUse, ADR 0005), so two would both
// work; the second would just be a needless request to the STS.
export const RENEW_WAIT_MS = 10_000;

// The automatic renew starts 60 s before the access token expires.
export const NEARLY_EXPIRED_SECONDS = 60;

// At most one automatic redirect to sign in per period, so a sign-in that keeps
// failing can never turn into a redirect loop.
export const REDIRECT_INTERVAL_MS = 5 * 60_000;

// Wake-up events and aca-app can ask for a check often; after a check that
// tried to renew and didn't get anywhere, the next one waits this long.
export const CHECK_INTERVAL_MS = 30_000;

// After SIGNIN the page navigates away. If it is still here after this long, the
// redirect didn't happen (signinRedirect failed) and checks may run again.
export const REDIRECT_TIMEOUT_MS = 30_000;

// How long a check waits after invalid_grant for a renew that may still be running.
export const REFRESH_RACE_WAIT_MS = 3_000;

export const REDIRECT_STORAGE_KEY = 'leerplatform.sessionExpiry.redirectedAt';

// The errors of a silent renew that mean only an interactive sign-in can help.
// invalid_grant is what a rejected refresh token gives.
const SIGN_IN_REQUIRED = ['login_required', 'interaction_required', 'invalid_grant'];

const signInRequired = (error: unknown) =>
    SIGN_IN_REQUIRED.includes((error as {error?: string})?.error ?? '');

const needsRenewal = (user: ExpiringUser | null, signedIn: boolean) =>
    user ? !!user.expired || (user.expires_in ?? Infinity) <= NEARLY_EXPIRED_SECONDS : signedIn;

function redirectedRecently(): boolean {
    try {
        const at = Number(window.sessionStorage.getItem(REDIRECT_STORAGE_KEY));

        // A time in the future (the clock went back) doesn't block anything.
        return at > 0 && at <= Date.now() && Date.now() - at < REDIRECT_INTERVAL_MS;
    } catch {
        return false;
    }
}

function rememberRedirect(): void {
    try {
        window.sessionStorage.setItem(REDIRECT_STORAGE_KEY, String(Date.now()));
    } catch {
        // Without sessionStorage there is no stored user either.
    }
}

// After a night of sleep with the tab open, the access token has expired and so
// has the session at the STS. The renew in the background then fails with
// login_required, and nothing used to happen after that: aca-app kept the page
// "signed in" and every API call returned 401 until the user pressed F5 (#7248).
//
// This sends the user to sign in once the token has really expired and renewing
// doesn't work. With SSO they're back on the same page within seconds: the
// SIGNIN saga passes terugkeerUrl() as the return address.
//
// Only a token that has really expired counts. In Safari and Firefox the renew
// through the iframe always fails, because the STS cookie isn't sent there;
// redirecting on that failure alone would send the user away while the token
// is still valid.
//
// Like stsSignOut.ts, it removes the stored user and dispatches SIGNIN, and
// deliberately doesn't set state.oidc.user to null.
export function connectSessionExpiry(
    userManager: SessionUserManager,
    dispatch: (action: {type: string}) => void,
): SessionExpiry {
    let running: Promise<void> | null = null;
    let lastCheckEndedAt = 0;
    let redirecting = false;
    let renewSettled: (() => void) | null = null;

    // The user as the events report it. Not getUser() on every check: that
    // re-arms the expiring and expired timers of oidc-client at 1 s, so in the
    // last minute before expiry every check would start the automatic renew again.
    let user: ExpiringUser | null = null;
    let userEventSeen = false;
    const initialUser = userManager
        .getUser()
        .then((stored) => {
            if (!userEventSeen) user = stored;
        })
        .catch(() => {});

    const waitForRenew = (ms: number) =>
        new Promise<void>((resolve) => {
            let timer: ReturnType<typeof setTimeout> | undefined;
            const done = () => {
                clearTimeout(timer);
                renewSettled = null;
                resolve();
            };

            timer = setTimeout(done, ms);
            renewSettled = done;
        });

    const redirectToSignIn = () => {
        if (redirectedRecently()) {
            trackAuthEvent('session expired, sign-in redirect skipped');
            return;
        }

        rememberRedirect();
        redirecting = true;
        setTimeout(() => {
            redirecting = false;
        }, REDIRECT_TIMEOUT_MS);
        trackAuthEvent('session expired, redirecting to sign-in');

        userManager
            .removeUser()
            .catch(() => {})
            .then(() => dispatch({type: 'SIGNIN'}));
    };

    const run = async (signedIn: boolean, renewFailed: boolean): Promise<boolean> => {
        await initialUser;

        if (!needsRenewal(user, signedIn)) return false;

        // Offline the STS can't be reached either; the online event checks again.
        if (!navigator.onLine) return false;

        // Still valid: the expired event comes later and checks again.
        if (renewFailed && user && !user.expired) return false;

        // Without a stored user there is no refresh token to renew with.
        if (!renewFailed && user) {
            await waitForRenew(RENEW_WAIT_MS);

            if (!needsRenewal(user, signedIn)) return false;

            try {
                await userManager.signinSilent();
                return false;
            } catch (error) {
                trackAuthEvent('session check renew failed', error);

                // A network error or timeout says nothing about the session.
                if (!signInRequired(error)) return true;

                // Before giving up on a rejected refresh token, give a renew that
                // is still running a moment to load its user.
                if ((error as {error?: string}).error === 'invalid_grant') {
                    await waitForRenew(REFRESH_RACE_WAIT_MS);
                }
            }

            // Still valid (Safari and Firefox before expiry): wait for the next check.
            if (user && !user.expired) return true;
        }

        redirectToSignIn();
        return true;
    };

    // A check that tried to renew and didn't get anywhere holds off the next one,
    // also when the events keep coming: while the stored user is expired, the
    // oidc middleware calls getUser() on every action, and that re-arms the
    // expired event at 1 s. A check that had nothing to do, or stopped because the
    // network was gone, doesn't hold off.
    const check = ({signedIn = false} = {}, {renewFailed = false, urgent = false} = {}) => {
        if (redirecting) return Promise.resolve();
        if (running) return running;
        if (!urgent && Date.now() - lastCheckEndedAt < CHECK_INTERVAL_MS) return Promise.resolve();

        running = run(signedIn, renewFailed)
            .then((holdOff) => {
                if (holdOff) lastCheckEndedAt = Date.now();
            })
            .catch((error: unknown) => trackAuthEvent('session check failed', error))
            .finally(() => {
                running = null;
            });

        return running;
    };

    userManager.events.addUserLoaded((loaded) => {
        userEventSeen = true;
        user = loaded;
        renewSettled?.();
    });

    userManager.events.addUserUnloaded(() => {
        userEventSeen = true;
        user = null;
    });

    userManager.events.addSilentRenewError((error) => {
        renewSettled?.();

        if (signInRequired(error)) void check({}, {renewFailed: true});
    });

    userManager.events.addAccessTokenExpired(() => void check());

    // The timers of oidc-client don't run during sleep, so check when the page
    // comes back, before the next calls go out with an expired token.
    const onWake = () => {
        if (document.visibilityState !== 'hidden') void check();
    };

    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    // A check that stopped offline didn't hold off, but one whose renew failed
    // on the network just before the connection dropped did.
    window.addEventListener('online', () => void check({}, {urgent: true}));

    return {check: (options) => check(options)};
}
